import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import './SimpleScan.css';
import { getPhoneId } from '../utils/phoneId';
import AgentSelector from '../components/AgentSelector';
import { API_URL } from '../config/api';

const SimpleScan = () => {
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [phoneId, setPhoneId] = useState(null);
  const [user, setUser] = useState(null);
  const [cameraPermission, setCameraPermission] = useState(null);
  const [creatingRequest, setCreatingRequest] = useState(false);
  const [showAgentSelector, setShowAgentSelector] = useState(false);
  const [pendingScanCode, setPendingScanCode] = useState(null); // Pour stocker le code QR en attente
  const html5QrCodeRef = useRef(null);
  const scanAttemptedRef = useRef(false);
  const requestInProgressRef = useRef(false); // Protection contre les doubles appels
  const processedQRCodesRef = useRef(new Set()); // QR codes déjà traités
  const useEffectExecutedRef = useRef(false); // Protection contre les doubles useEffect

  useEffect(() => {
    // Protection contre les doubles exécutions (React Strict Mode)
    if (useEffectExecutedRef.current) {
      return;
    }
    useEffectExecutedRef.current = true;

    let timer = null;
    
    // Obtenir l'identifiant du téléphone
    const id = getPhoneId();
    setPhoneId(id);

    // Vérifier si on arrive depuis un QR code avec paramètre ?scan=
    const urlParams = new URLSearchParams(window.location.search);
    const scanParam = urlParams.get('scan');
    
    if (scanParam) {
      // Si on a un paramètre scan dans l'URL, stocker le code pour utilisation ultérieure
      setPendingScanCode(scanParam);
      
      if (id) {
        identifyUser(id).then((userData) => {
          if (userData) {
            // Utilisateur identifié, créer directement la demande
            createRequestDirectly(id, scanParam);
          } else {
            // Utilisateur non identifié, afficher le sélecteur
            // Le scanParam sera utilisé après la sélection
            setShowAgentSelector(true);
          }
        });
      } else {
        // Pas de phone_id, afficher le sélecteur
        setShowAgentSelector(true);
      }
      return;
    }

    // Identifier l'utilisateur automatiquement (cas normal sans paramètre scan)
    if (id) {
      identifyUser(id).then((userData) => {
        // Si l'utilisateur est identifié, continuer le flux normal
        if (userData) {
          // Vérifier si l'utilisateur arrive depuis le QR code (paramètre ?request=general - ancien format)
          if (urlParams.get('request') === 'general') {
            // Créer automatiquement la demande sans scanner
            createRequestDirectly(id);
          } else {
            // Sinon, démarrer le scan normal
            timer = setTimeout(() => {
              if (!scanAttemptedRef.current) {
                startScanAuto();
              }
            }, 300);
          }
        }
        // Si userData est null, le sélecteur d'agents sera affiché
      });
    }

    return () => {
      if (timer) clearTimeout(timer);
      // Nettoyer le scanner lors du démontage
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const identifyUser = async (phoneId) => {
    try {
      const response = await fetch(`${API_URL}/api/users/identify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone_id: phoneId }),
      });

      if (response.ok) {
        const userData = await response.json();
        if (userData) {
          // Utilisateur trouvé (même temporaire) - permettre le scan
          // Le sélecteur s'affichera APRÈS le scan si c'est un compte temporaire
          setUser(userData);
          setShowAgentSelector(false);
          return userData;
        } else {
          // Utilisateur non trouvé - afficher le sélecteur d'agents
          setShowAgentSelector(true);
          return null;
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Erreur inconnue' }));
        console.error('Erreur API identify:', response.status, errorData);
        setError(`Erreur ${response.status}: ${errorData.error || 'Impossible de s\'identifier'}`);
        setShowAgentSelector(true);
        return null;
      }
    } catch (error) {
      console.error('Erreur lors de l\'identification:', error);
      setError(`Erreur de connexion au serveur (${API_URL}). Vérifiez que le serveur est démarré et que vous êtes sur le même réseau.`);
      setShowAgentSelector(true);
      return null;
    }
  };

  const handleAgentSelected = async (selectedUser) => {
    setUser(selectedUser);
    setShowAgentSelector(false);
    
    // Si on avait un scan en attente (compte temporaire après scan caméra), afficher le résultat maintenant
    if (result && result.needsAccountSelection) {
      // Le scan a déjà été fait, juste mettre à jour le message
      setResult({
        ...result,
        success: true,
        message: 'C\'est fait !',
        needsAccountSelection: false
      });
    } 
    // Si on avait un code QR en attente (depuis paramètre ?scan=)
    else if (pendingScanCode) {
      // Créer la demande directement avec le code QR stocké
      await createRequestDirectly(phoneId, pendingScanCode);
      setPendingScanCode(null); // Nettoyer après utilisation
    } 
    // Sinon, démarrer le scan automatiquement (première connexion normale)
    else {
      setTimeout(() => {
        if (!scanAttemptedRef.current) {
          startScanAuto();
        }
      }, 300);
    }
  };

  // Fonction supprimée : Les utilisateurs ne peuvent plus délier leur téléphone eux-mêmes
  // Seul l'admin peut le faire depuis l'interface de gestion des utilisateurs

  const createRequestDirectly = async (phoneId, qrCode = 'EPI-REQUEST') => {
    // Protection contre les doubles appels
    if (requestInProgressRef.current) {
      console.log('⚠️ Une demande est déjà en cours, ignore...');
      return;
    }

    // Vérifier si ce QR code a déjà été traité
    const requestKey = `${phoneId}-${qrCode}`;
    if (processedQRCodesRef.current.has(requestKey)) {
      console.log('⚠️ Ce QR code a déjà été traité, ignore...');
      return;
    }

    try {
      requestInProgressRef.current = true;
      processedQRCodesRef.current.add(requestKey);
      setError(null);
      setCreatingRequest(true);
      
      // Créer directement la demande avec le QR code
      const scanResponse = await fetch(`${API_URL}/api/scans/scan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          qr_code: qrCode,
          phone_id: phoneId
        }),
      });

      const scanData = await scanResponse.json();
      setCreatingRequest(false);
      requestInProgressRef.current = false;

      if (scanResponse.ok) {
        setResult({
          success: true,
          message: 'C\'est fait !',
          request: scanData
        });
      } else {
        // En cas d'erreur, retirer de la liste pour permettre un nouvel essai
        processedQRCodesRef.current.delete(requestKey);
        setResult({
          success: false,
          message: scanData.error || 'Erreur lors de l\'envoi',
          details: scanData
        });
      }
    } catch (err) {
      console.error('Erreur lors du traitement:', err);
      setCreatingRequest(false);
      requestInProgressRef.current = false;
      // En cas d'erreur, retirer de la liste pour permettre un nouvel essai
      processedQRCodesRef.current.delete(requestKey);
      setError('Erreur de connexion. Vérifiez votre internet.');
    }
  };

  const startScanAuto = async () => {
    if (scanAttemptedRef.current) return;
    scanAttemptedRef.current = true;

    if (!phoneId) {
      setError('Initialisation en cours...');
      return;
    }

    try {
      setError(null);
      setResult(null);
      setScanning(true);
      setCameraPermission('requesting');

      const html5QrCode = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = html5QrCode;

      // Déterminer la taille de la qrbox selon la taille de l'écran
      const isMobile = window.innerWidth <= 480;
      const qrboxSize = isMobile ? 250 : 280;

      // Essayer d'abord la caméra arrière (environment)
      try {
        await html5QrCode.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: qrboxSize, height: qrboxSize },
            aspectRatio: 1.0
          },
          (decodedText) => {
            handleScan(decodedText);
          },
          (errorMessage) => {
            // Ignorer les erreurs de scan continu (normal pendant la recherche)
          }
        );
        setCameraPermission('granted');
      } catch (err) {
        // Si la caméra arrière ne fonctionne pas, essayer la caméra avant
        try {
          await html5QrCode.start(
            { facingMode: "user" },
            {
              fps: 10,
              qrbox: { width: qrboxSize, height: qrboxSize },
              aspectRatio: 1.0
            },
            (decodedText) => {
              handleScan(decodedText);
            },
            (errorMessage) => {
              // Ignorer les erreurs de scan continu
            }
          );
          setCameraPermission('granted');
        } catch (err2) {
          setCameraPermission('denied');
          setError('Impossible d\'accéder à la caméra. Veuillez autoriser l\'accès à la caméra dans les paramètres de votre navigateur.');
          setScanning(false);
        }
      }
    } catch (err) {
      console.error('Erreur lors du démarrage du scan:', err);
      setCameraPermission('denied');
      setError('Impossible de démarrer la caméra. Veuillez autoriser l\'accès à la caméra.');
      setScanning(false);
    }
  };

  const handleScan = async (qrCode) => {
    // Protection contre les doubles scans
    if (requestInProgressRef.current) {
      console.log('⚠️ Un scan est déjà en cours, ignore...');
      return;
    }

    // Vérifier si ce QR code a déjà été traité
    const requestKey = `${phoneId}-${qrCode}`;
    if (processedQRCodesRef.current.has(requestKey)) {
      console.log('⚠️ Ce QR code a déjà été scanné, ignore...');
      return;
    }

    // Arrêter le scanner après avoir détecté un QR code
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        await html5QrCodeRef.current.clear();
      } catch (err) {
        console.error('Erreur lors de l\'arrêt:', err);
      }
      html5QrCodeRef.current = null;
    }
    setScanning(false);

    try {
      requestInProgressRef.current = true;
      processedQRCodesRef.current.add(requestKey);

      // Créer une demande
      const scanResponse = await fetch(`${API_URL}/api/scans/scan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          qr_code: qrCode,
          phone_id: phoneId
        }),
      });

      const scanData = await scanResponse.json();
      requestInProgressRef.current = false;

      if (scanResponse.ok) {
        // Vérifier si l'utilisateur a un compte temporaire
        // Si oui, afficher le sélecteur d'agents pour qu'il choisisse son nom
        if (user && user.name && user.name.startsWith('Utilisateur PHONE-')) {
          // Sauvegarder les données du scan pour les utiliser après la sélection
          setResult({
            success: true,
            message: 'QR code scanné !',
            request: scanData,
            needsAccountSelection: true
          });
          // Afficher le sélecteur d'agents
          setShowAgentSelector(true);
        } else {
          // Compte normal - afficher le résultat normalement
          setResult({
            success: true,
            message: 'C\'est fait !',
            request: scanData
          });
        }
      } else {
        // En cas d'erreur, retirer de la liste pour permettre un nouvel essai
        processedQRCodesRef.current.delete(requestKey);
        setResult({
          success: false,
          message: scanData.error || 'Erreur lors de l\'envoi',
          details: scanData
        });
      }
    } catch (err) {
      console.error('Erreur lors du traitement:', err);
      requestInProgressRef.current = false;
      // En cas d'erreur, retirer de la liste pour permettre un nouvel essai
      processedQRCodesRef.current.delete(requestKey);
      setError('Erreur de connexion. Vérifiez votre internet.');
    }
  };

  const retryScan = () => {
    setResult(null);
    setError(null);
    scanAttemptedRef.current = false;
    startScanAuto();
  };

  // Si l'utilisateur n'est pas identifié, afficher le sélecteur d'agents AVANT le scan
  // Si showAgentSelector est true ET qu'on a un résultat avec needsAccountSelection, c'est après le scan
  if (showAgentSelector) {
    const isTemporaryAccount = user && user.name && user.name.startsWith('Utilisateur PHONE-');
    const isAfterScan = result && result.needsAccountSelection;
    
    return (
      <div className="simple-scan">
        <div className="scan-header">
          <h1>🔐 {isAfterScan ? 'Choisissez votre compte' : isTemporaryAccount ? 'Choisissez votre compte' : 'Première connexion'}</h1>
          <p className="instructions">
            {isAfterScan
              ? 'QR code scanné ! Veuillez maintenant choisir votre nom dans la liste des agents pour finaliser votre demande.'
              : isTemporaryAccount 
                ? 'Votre téléphone est lié à un compte temporaire. Vous pouvez scanner, puis choisir votre nom après.'
                : 'Avant de scanner, identifiez-vous pour lier votre téléphone à votre compte'}
          </p>
          {isTemporaryAccount && user && !isAfterScan && (
            <p className="temporary-account-info">
              Compte actuel: <strong>{user.name}</strong> - Vous pouvez scanner maintenant
            </p>
          )}
          {!isTemporaryAccount && !isAfterScan && (
            <p className="permanent-link-info" style={{ 
              color: '#4CAF50', 
              fontWeight: 'bold',
              marginTop: '1rem',
              padding: '0.75rem',
              background: 'rgba(76, 175, 80, 0.1)',
              borderRadius: '8px'
            }}>
              ⚠️ Important : Une fois votre profil sélectionné, il sera lié de manière permanente à votre téléphone. 
              Seul un administrateur pourra modifier cette liaison si vous changez de téléphone.
            </p>
          )}
          {isAfterScan && result && result.request && (
            <div className="scan-success-info">
              <p>✅ QR code scanné avec succès</p>
              <p>Numéro de demande: #{result.request.request_id}</p>
            </div>
          )}
        </div>
        <AgentSelector phoneId={phoneId} onAgentSelected={handleAgentSelected} />
      </div>
    );
  }

  return (
    <div className="simple-scan">
      <div className="scan-header">
        <h1>📷 Utilisez votre appareil photo</h1>
        {user ? (
          <div className="user-info-container">
            <p className="user-info">Connecté en tant que: <strong>{user.name}</strong></p>
            {user.name && user.name.startsWith('Utilisateur PHONE-') && (
              <div className="user-warning" style={{
                padding: '0.75rem',
                background: '#fff3cd',
                borderRadius: '8px',
                marginTop: '0.5rem'
              }}>
                <p>⚠️ Vous êtes connecté avec un compte temporaire.</p>
                <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                  Contactez un administrateur pour lier votre téléphone à votre profil. 
                  Une fois lié, cette liaison sera permanente.
                </p>
              </div>
            )}
            {/* Les utilisateurs ne peuvent plus changer leur profil - seul l'admin peut le faire */}
          </div>
        ) : (
          <p className="instructions">Pointez vers le code QR affiché</p>
        )}
      </div>

      {!result && !creatingRequest && (
        <div className="scanner-wrapper">
          {scanning && cameraPermission === 'granted' ? (
            <div className="scanner-container">
              <div id="qr-reader" className="qr-reader"></div>
              <div className="scan-overlay">
                <div className="scan-frame"></div>
                <p className="scan-hint">Pointez votre téléphone vers le code QR</p>
              </div>
            </div>
          ) : scanning && cameraPermission === 'requesting' ? (
            <div className="loading-camera">
              <div className="spinner"></div>
              <p>Ouverture de l'appareil photo...</p>
              <p className="hint">Autorisez l'accès à l'appareil photo si demandé</p>
            </div>
          ) : (
            <div className="loading-camera">
              <div className="spinner"></div>
              <p>Préparation de l'appareil photo...</p>
            </div>
          )}
        </div>
      )}

      {creatingRequest && (
        <div className="scanner-wrapper">
          <div className="loading-camera">
            <div className="spinner"></div>
            <p>Envoi de votre demande...</p>
            <p className="hint">Veuillez patienter</p>
          </div>
        </div>
      )}

      {error && !scanning && (
        <div className="error-message">
          <p>⚠️ {error}</p>
          <button className="btn-retry" onClick={retryScan}>
            Réessayer
          </button>
        </div>
      )}

      {result && (
        <div className={`result-message ${result.success ? 'success' : 'error'}`}>
          {result.success && user && user.name && user.name.startsWith('Utilisateur PHONE-') && (
            <div className="result-warning">
              <p>⚠️ Vous êtes connecté avec un compte temporaire.</p>
              <p>Pour que vos demandes soient associées à votre nom, veuillez choisir votre compte maintenant.</p>
              <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                Une fois votre profil sélectionné, il sera lié de manière permanente. 
                Contactez un administrateur si vous devez changer de téléphone.
              </p>
            </div>
          )}
          <div className="result-icon">
            {result.success ? '✅' : '❌'}
          </div>
          <h2>{result.success ? 'Demande envoyée !' : 'Erreur'}</h2>
          <p>{result.message}</p>
          
          {result.success ? (
            <div className="success-details">
              <p>✅ Votre demande a été envoyée</p>
              <p>L'administrateur va vous donner l'élément.</p>
              <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666' }}>
                Pour faire une nouvelle demande, rescannez le QR code.
              </p>
            </div>
          ) : (
            <button className="btn-new-scan" onClick={retryScan}>
              Réessayer
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SimpleScan;

